import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MODULE_META } from './moduleMeta';
import { useAuth } from '../../contexts/AuthContext';
import { userHasAnyMenuIn, RbacUser } from '../../auth/menuHelpers';
import './AppShell.css';

export interface ModuleSwitcherProps {
  onClose: () => void;
  /** Bubbles up so the phone drawer can close after picking a module. */
  onCloseMobile: () => void;
}

const ModuleSwitcher: React.FC<ModuleSwitcherProps> = ({ onClose, onCloseMobile }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    function handleClickAway(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener('keydown', handleEsc);
    document.addEventListener('mousedown', handleClickAway);
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.removeEventListener('mousedown', handleClickAway);
    };
  }, [onClose]);

  // Focus management: move focus into popover on mount, restore on unmount.
  useEffect(() => {
    triggerRef.current = document.activeElement as HTMLElement | null;
    const firstFocusable = containerRef.current?.querySelector<HTMLElement>(
      'a, button, [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();
    return () => {
      triggerRef.current?.focus();
    };
  }, []);

  const handleSelect = () => {
    onClose();
    onCloseMobile();
  };

  return (
    <div className="shell-switcher" role="dialog" aria-modal="true" aria-label={t('shell.switchModule')} ref={containerRef}>
      <ul className="shell-switcher__list" role="menu">
        {MODULE_META.map(meta => {
          const accessible = userHasAnyMenuIn(user as RbacUser | null, meta.moduleKey);
          const visible = accessible || meta.disabled;
          if (!visible) return null;
          const isCurrent = pathname === meta.to || pathname.startsWith(meta.to + '/');
          const isDisabled = meta.disabled || !accessible;
          const className = `shell-switcher-item${isCurrent ? ' shell-switcher-item--current' : ''}${isDisabled ? ' shell-switcher-item--disabled' : ''}`;
          if (isDisabled) {
            return (
              <li key={meta.id} className={className} role="menuitem" aria-disabled="true">
                <span className="shell-switcher-item__emoji" aria-hidden="true">{meta.emoji}</span>
                <span className="shell-switcher-item__text">
                  <span className="shell-switcher-item__name">{t(meta.labelKey)}</span>
                  {meta.disabled && <span className="shell-switcher-item__badge">{t('home.modules.comingSoon', 'Coming Soon')}</span>}
                </span>
              </li>
            );
          }
          return (
            <li key={meta.id} className={className} role="menuitem">
              <Link
                to={meta.to}
                className="shell-switcher-item__link"
                onClick={handleSelect}
                {...(isCurrent ? { 'aria-current': 'page' as const } : {})}
              >
                <span className="shell-switcher-item__emoji" aria-hidden="true">{meta.emoji}</span>
                <span className="shell-switcher-item__text">
                  <span className="shell-switcher-item__name">{t(meta.labelKey)}</span>
                </span>
                {isCurrent && <i className="bi bi-check-lg" aria-hidden="true" />}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ModuleSwitcher;
